import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import IconButton from "@mui/material/IconButton";
import { FaTimes } from "react-icons/fa";
import { AiOutlinePlusCircle } from "react-icons/ai";
import axios from "axios";
import BtnLoader from "Components/Common/Loader/BtnLoader";
import { featchPromoPlan } from "Common Usage/apiService";
import { loadDecrypted } from "Common Usage/localStorageUtils";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: 500,
  bgcolor: "background.paper",
  border: "none",
  borderRadius: 8,
  boxShadow: 24,
  p: 4,
};

const AddCoinsForm = ({
  addEmojiModal,
  handleAddModalClose,
  fetchCoinPlans,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    promoCodes: [],
    description: "",
    purchaseAmount: "",
    coinAmount: "",
    currency: "INR",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [promoPlans, setPromoPlans] = useState([]);

  const loggedInUser = loadDecrypted("HeartConnect-StaffData");
  const staffData = Array.isArray(loggedInUser)
    ? loggedInUser[0]
    : loggedInUser;
  const staffId = staffData?._id || staffData?.id || "";

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handlePromoChange = (index, e) => {
    const updatedPromoCodes = [...formData.promoCodes];
    updatedPromoCodes[index] = e.target.value;
    setFormData({
      ...formData,
      promoCodes: updatedPromoCodes.filter(Boolean), // Filter out empty values
    });
  };

  const addPromoField = () => {
    setFormData({
      ...formData,
      promoCodes: [...formData.promoCodes, ""],
    });
  };

  const fetchPromos = async () => {
    try {
      const data = await featchPromoPlan();
      setPromoPlans(data);
    } catch (error) {
      console.error("Error fetching promo codes:", error);
    }
  };

  const createStaffActivityLog = async (actionType, description) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/staffs-activity-log/create-staffs-activity`,
        {
          staff: staffId,
          action: actionType,
          description,
        }
      );
    } catch (error) {
      console.error("Activity log failed:", error.message);
    }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    setSuccess("");

    try {
      const config = {
        method: "post",
        url: `${process.env.REACT_APP_API_URL}/coin-plan/createcoin-plans`,
        headers: {
          "Content-Type": "application/json",
        },
        data: JSON.stringify(formData),
      };

      await axios.request(config);

      // ✅ Activity Log Integration
      await createStaffActivityLog(
        "CREATE_COIN_PLAN",
        `Coin package "${formData.title}" created (Amount: ₹${
          formData.purchaseAmount
        }, Coins: ${formData.coinAmount}, Promos: ${
          formData.promoCodes.length > 0
            ? formData.promoCodes.length + " applied"
            : "No promo"
        })`
      );

      setSuccess("Coin plan created successfully!");
      fetchCoinPlans();
      handleAddModalClose();

      setFormData({
        title: "",
        promoCodes: [],
        description: "",
        purchaseAmount: "",
        coinAmount: "",
        currency: "INR",
      });
    } catch (error) {
      console.error(error);
      setError("Failed to create coin plan. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (addEmojiModal) {
      fetchPromos();
    }
  }, [addEmojiModal]);

  return (
    <Modal open={addEmojiModal} onClose={handleAddModalClose}>
      <Box sx={style}>
        <IconButton
          onClick={handleAddModalClose}
          sx={{ position: "absolute", right: 8, top: 8, color: "#c51369" }}
        >
          <FaTimes />
        </IconButton>
        <div className="mx-auto">
          <form onSubmit={handleSubmit} className="space-y-6">
            <h2 className="mb-6 text-center text-2xl font-bold text-navy-700">
              Add Coins Package
            </h2>
            {error && <p className="mb-4 text-center text-red-500">{error}</p>}
            {success && (
              <p className="mb-4 text-center text-green-500">{success}</p>
            )}

            <div className="space-y-4">
              <div>
                <label
                  htmlFor="title"
                  className="mb-2 block text-lg text-navy-700"
                >
                  Package Name
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className="w-full rounded-md border border-gray-300 p-2 focus:ring-2 focus:ring-[#c51369]"
                  required
                />
              </div>
              <div>
                <label
                  htmlFor="description"
                  className="mb-2 block text-lg text-navy-700"
                >
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  className="w-full rounded-md border border-gray-300 p-2 focus:ring-2 focus:ring-[#c51369]"
                  rows={3}
                  required
                />
              </div>
              <div>
                <label className="mb-2 block text-lg text-navy-700">
                  Promo Codes
                </label>
                {formData.promoCodes.map((promoCode, index) => (
                  <select
                    key={index}
                    value={promoCode}
                    onChange={(e) => handlePromoChange(index, e)}
                    className="mt-2 w-full rounded-md border border-gray-300 p-2 focus:ring-2 focus:ring-[#c51369]"
                  >
                    <option value="">Select a promo code</option>
                    {promoPlans.map((promo) => (
                      <option key={promo._id} value={promo._id}>
                        {promo.code} - {promo.discount}%
                      </option>
                    ))}
                  </select>
                ))}
                <button
                  type="button"
                  onClick={addPromoField}
                  className="mt-2 flex items-center text-[#c51369]"
                >
                  <AiOutlinePlusCircle size={30} className="mr-2" /> Add Promo
                  Code
                </button>
              </div>
              <div className="flex space-x-4">
                <div className="flex-1">
                  <label
                    htmlFor="purchaseAmount"
                    className="mb-2 block text-lg text-navy-700"
                  >
                    Amount (INR)
                  </label>
                  <input
                    type="number"
                    id="purchaseAmount"
                    name="purchaseAmount"
                    value={formData.purchaseAmount}
                    onChange={handleChange}
                    className="w-full rounded-md border border-gray-300 p-2 focus:ring-2 focus:ring-[#c51369]"
                    required
                  />
                </div>
                <div className="flex-1">
                  <label
                    htmlFor="coinAmount"
                    className="mb-2 block text-lg text-navy-700"
                  >
                    Coin Amount
                  </label>
                  <input
                    type="number"
                    id="coinAmount"
                    name="coinAmount"
                    value={formData.coinAmount}
                    onChange={handleChange}
                    className="w-full rounded-md border border-gray-300 p-2 focus:ring-2 focus:ring-[#c51369]"
                    required
                  />
                </div>
              </div>
            </div>
            <button
              type="submit"
              className="w-full rounded-md bg-[#c51369] p-3 text-white hover:bg-[#a10f5b]"
            >
              {isLoading ? <BtnLoader /> : "Create Package"}
            </button>
          </form>
        </div>
      </Box>
    </Modal>
  );
};

export default AddCoinsForm;
